import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent, PanelComponent} from "@components";
import {animate, transition, trigger, style} from '@angular/animations';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {
  EventItem,
  EventPaymentSettings, Language, Lookups,
  OrganiserPaymentData,
  Race,
  RaceAdditionalPricingItem,
  RacePricingItemValue, RegistrationDisplayFlags, VisibilityTypes, TOAST_STATE
} from "@model";
import {RaceCommercialService, ToastService} from "@services";
import {required} from "@utility";
import {ActivatedRoute} from '@angular/router';
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";


@Component({
  selector: 'app-race-additional-pricing-item-form',
  templateUrl: './race-additional-pricing-item-form.component.html',
  styleUrls: ['./race-additional-pricing-item-form.component.scss'],
  animations: [
    trigger('itemAnimation', [
      transition(':enter', [
        style({opacity: 0,}),
        animate('100ms', style({opacity: 1,}))
      ]),
      transition(':leave', [
        animate('100ms', style({opacity: 0,}))
      ])
    ])
  ]
})
export class RaceAdditionalPricingItemFormComponent extends AbstractComponent implements OnInit {
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() disableEditing: boolean = false;
  @Input() currentRaceAdditionalItem: RaceAdditionalPricingItem = new RaceAdditionalPricingItem();
  @Input() eventPaymentSettings: EventPaymentSettings | null = null;
  @Input() organiserPaymentData: OrganiserPaymentData | null = null;
  @Input() raceAdditionalItems: RaceAdditionalPricingItem[] = [];

  @Input() currencies: Lookups[] = [];
  @Input() visibilityTypes: Lookups[] = [];
  @Input() registrationDisplayFlags: Lookups[] = [];
  @Input() title: string | undefined

  currentPricingItemValues: RacePricingItemValue[] = [];


  currency: string = "";
  priceConstantSubscription: Subscription | undefined;

  inputForm = new FormGroup({
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    nameSr: new FormControl(),
    nameBs: new FormControl(),
    internalDescription: new FormControl(),
    additionalTextField: new FormControl(),
    priceConstant: new FormControl(),
    fieldRequiredForRegistration: new FormControl(),
    pricingItemValues: this.formBuilder.array([]),
    fieldVisibility: new FormControl(),
    fieldRegistrationDisplayFlag: new FormControl(),
    vat: new FormControl(),
    vatExplanation: new FormControl(),
    vatExplanationEng: new FormControl(),
    vatExplanationSlo: new FormControl(),
    vatExplanationIta: new FormControl(),
    vatExplanationSr: new FormControl(),
    vatExplanationBs: new FormControl(),
    price: new FormControl(),
  });

  constructor(public translocoService: TranslocoService, public formBuilder: FormBuilder, public activeModal: NgbActiveModal,
              private toastService: ToastService) {
    super();
  }

  ngOnInit(): void {
    this.currency = this.getCurrencyLabel();
    this.subscribeToPriceValueChange(this.inputForm.controls.price);
    this.subscribeToVATChanges();
    this.subscribeToFieldRequiredChange();
    this.initForm();
    this.onEditPricingAdditionalItem(this.currentRaceAdditionalItem);
  }


  private subscribeToVATChanges(): void {
    this.subscriptions.push(
      this.inputForm.controls.vat.valueChanges.subscribe((vat) => {
        if (vat && this.vatExplanationControlWriteable()) {
          this.enableVatExplanation();
        } else {
          this.disableVatExplanation();
        }
      })
    );
  }

  private subscribeToFieldRequiredChange(): void {
    this.subscriptions.push(
      this.inputForm.controls.fieldRequiredForRegistration.valueChanges.subscribe(required => {
        if (required) {
          this.inputForm.controls.fieldRegistrationDisplayFlag.setValue(RegistrationDisplayFlags.ALWAYS_USED);
          this.inputForm.controls.fieldRegistrationDisplayFlag.disable();
        } else if (this.areAllPricesZero()) {
          this.inputForm.controls.fieldRegistrationDisplayFlag.enable();
        }
      })
    )
  }

  private areAllPricesZero() {
    let allPricesZero = false;
    if (this.inputForm.controls.priceConstant.value) {
      if (!this.inputForm.controls.price.value) {
        allPricesZero = true;
      }
    } else {
      allPricesZero = true;
      for (let i = 0; i < this.currentPricingItemValues.length; i++) {
        if (this.pricingItemValues.at(i)?.get("price")?.value) {
          allPricesZero = false;
          break;
        }
      }
    }
    return allPricesZero;
  }

  private subscribeToPriceValueChange(formControl: FormControl) {
    this.subscriptions.push(formControl.valueChanges.subscribe((_) => {
      this.setPriceDependantFields()
    }));
  }

  private setPriceDependantFields() {
    if (this.areAllPricesZero()) {
      this.inputForm.controls.vat.removeValidators([required]);
      if (!(this.inputForm.controls.fieldRequiredForRegistration.value === true)) {
        this.inputForm.controls.fieldRegistrationDisplayFlag.enable();
      }
    } else {
      this.inputForm.controls.vat.addValidators(required);
      if (this.vatExplanationControlWriteable()) {
        this.enableVatExplanation()
      }
      this.inputForm.controls.fieldRegistrationDisplayFlag.setValue(RegistrationDisplayFlags.ALWAYS_USED);
      this.inputForm.controls.fieldRegistrationDisplayFlag.disable();
    }
    this.inputForm.updateValueAndValidity();
  }


  private initForm() {
    this.inputForm.controls.name.setValidators([required]);
    this.inputForm.controls.internalDescription.setValidators([required]);
    this.inputForm.controls.fieldRegistrationDisplayFlag.setValidators([required]);
    this.inputForm.controls.fieldVisibility.setValidators([required]);
    this.inputForm.controls.vat.setValidators([Validators.min(0), Validators.max(100)])
  }

  getCurrencyLabel(): string {

    const curr = this.currencies.find((currency) => currency.id === this.event?.currency);
    return curr?.name ?? "-";
  }

  private setValidatorsOnPriceBasedOnPriceConstant(isConstant: boolean) {
    if (!isConstant) {
      this.inputForm.controls.price.removeValidators(required);
      this.inputForm.controls.price.setValue(undefined);
    } else {
      this.inputForm.controls.price.addValidators(required);
      this.inputForm.controls.price.markAsUntouched();
      this.inputForm.controls.price.updateValueAndValidity()
    }
    if (this.inputForm.controls.pricingItemValues && this.inputForm.controls.pricingItemValues.length > 0) {
      for (var i: number = 0; i < this.inputForm.controls.pricingItemValues.length; i++) {
        let pricingItemValueFormGroup: FormGroup = this.pricingItemValueFormControl(i);
        if (isConstant) {
          pricingItemValueFormGroup.controls["price"].removeValidators(required);
          pricingItemValueFormGroup.controls["price"].setValue(undefined);
          pricingItemValueFormGroup.controls["price"].disable();
        } else {
          pricingItemValueFormGroup.controls["price"].addValidators(required);
          pricingItemValueFormGroup.controls["price"].enable();
        }
        pricingItemValueFormGroup.controls["price"].markAsUntouched();
        pricingItemValueFormGroup.controls["price"].updateValueAndValidity();
      }
    }
    this.inputForm.updateValueAndValidity();
  }

  private setValidatorsOnPricingItemValuesCount() {
    if (this.pricingItemValues.length > 0) {
      this.inputForm.controls.priceConstant.enable();
    } else {
      this.inputForm.controls.priceConstant.setValue(true);
      this.inputForm.controls.priceConstant.disable();
    }
  }

  private vatExplanationControlWriteable() {
    return this.organiserPaymentData?.paysPDV === true && this.eventPaymentSettings?.issuingInvoice === true;
  }

  private disableVatExplanation() {
    this.inputForm.controls.vatExplanation.setValue(undefined);
    this.inputForm.controls.vatExplanationEng.setValue(undefined);
    this.inputForm.controls.vatExplanationIta.setValue(undefined);
    this.inputForm.controls.vatExplanationSlo.setValue(undefined);
    this.inputForm.controls.vatExplanationBs.setValue(undefined);
    this.inputForm.controls.vatExplanationSr.setValue(undefined);
    this.inputForm.controls.vatExplanation.disable();
    this.inputForm.controls.vatExplanationEng.disable();
    this.inputForm.controls.vatExplanationIta.disable();
    this.inputForm.controls.vatExplanationSlo.disable();
    this.inputForm.controls.vatExplanationBs.disable();
    this.inputForm.controls.vatExplanationSr.disable();
  }

  private enableVatExplanation() {
    this.inputForm.controls.vatExplanation.enable();
    this.inputForm.controls.vatExplanationEng.enable();
    this.inputForm.controls.vatExplanationIta.enable();
    this.inputForm.controls.vatExplanationSlo.enable();
    this.inputForm.controls.vatExplanationSr.enable();
    this.inputForm.controls.vatExplanationBs.enable();
  }

  private createItemValueForm(): FormGroup {
    let formGroup = this.formBuilder.group({
      name: [''],
      nameEng: [''],
      nameIta: [''],
      nameSlo: [''],
      nameSr: [''],
      nameBs: [''],
      price: [undefined]
    })
    this.subscribeToPriceValueChange(formGroup.controls.price);
    if (this.inputForm.controls.priceConstant.value === false) {
      formGroup.controls.price.addValidators(required);
    } else {
      formGroup.controls.price.disable();
    }
    return formGroup;
  }

  get pricingItemValues(): FormArray {
    return this.inputForm.controls.pricingItemValues as FormArray;
  }

  pricingItemValueFormControl(i: number): FormGroup {
    return (this.inputForm.controls.pricingItemValues.controls.at(i) ?? this.createItemValueForm()
    ) as FormGroup;
  }


  addPricingItemValue() {
    let pricingItemFrom = this.createItemValueForm()
    this.subscriptions.push(pricingItemFrom.valueChanges.subscribe(_ => {
      this.inputForm.controls.pricingItemValues.updateValueAndValidity();
      this.inputForm.updateValueAndValidity()
    }))
    this.pricingItemValues.push(pricingItemFrom);
    this.currentPricingItemValues.push(new RacePricingItemValue());
    this.setValidatorsOnPricingItemValuesCount();
    this.inputForm.updateValueAndValidity();
  }

  deletePricingItemValue(index: number) {
    this.pricingItemValues.removeAt(index);
    this.currentPricingItemValues.splice(index, 1);
    this.setValidatorsOnPricingItemValuesCount();
    this.inputForm.updateValueAndValidity();
  }

  onSave(): void {
    if (!this.inputForm.valid) {
      this.markAllAsTouched(this.inputForm)
      return;
    } else if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    } else {
      let raceAdditionalPricingItem: RaceAdditionalPricingItem = this.getRaceAdditionalPricingItemFields();
      raceAdditionalPricingItem.raceId = this.race?.id;
      this.activeModal.close(raceAdditionalPricingItem)
    }
  }


  getRaceAdditionalPricingItemFields(): RaceAdditionalPricingItem {
    this.checkValidators(this.inputForm);
    let raceAdditionalPricingItem: RaceAdditionalPricingItem = {...this.currentRaceAdditionalItem}
    raceAdditionalPricingItem.name = this.inputForm.value.name;
    raceAdditionalPricingItem.nameEng = this.inputForm.value.nameEng;
    raceAdditionalPricingItem.nameIta = this.inputForm.value.nameIta;
    raceAdditionalPricingItem.nameSlo = this.inputForm.value.nameSlo;
    raceAdditionalPricingItem.nameSr = this.inputForm.value.nameSr;
    raceAdditionalPricingItem.nameBs = this.inputForm.value.nameBs;
    raceAdditionalPricingItem.internalDescription = this.inputForm.value.internalDescription;
    raceAdditionalPricingItem.additionalTextField = this.inputForm.value.additionalTextField;
    raceAdditionalPricingItem.priceConstant = this.inputForm.value.priceConstant;
    raceAdditionalPricingItem.fieldRequiredForRegistration = this.inputForm.value.fieldRequiredForRegistration;
    raceAdditionalPricingItem.fieldVisibility = this.inputForm.value.fieldVisibility ?? VisibilityTypes.NOT_VISIBLE;
    raceAdditionalPricingItem.fieldRegistrationDisplayFlag = this.inputForm.controls.fieldRegistrationDisplayFlag.value ?? RegistrationDisplayFlags.NOT_USED;
    if (!this.areAllPricesZero()) {
      raceAdditionalPricingItem.vat = Number(this.inputForm.value.vat);
      if (this.vatExplanationControlWriteable()) {
        raceAdditionalPricingItem.vatExplanation = this.inputForm.value.vatExplanation;
        raceAdditionalPricingItem.vatExplanationEng = this.inputForm.value.vatExplanationEng;
        raceAdditionalPricingItem.vatExplanationSlo = this.inputForm.value.vatExplanationSlo;
        raceAdditionalPricingItem.vatExplanationSr = this.inputForm.value.vatExplanationSr;
        raceAdditionalPricingItem.vatExplanationBs = this.inputForm.value.vatExplanationBs;
        raceAdditionalPricingItem.vatExplanationIta = this.inputForm.value.vatExplanationIta;
      }
    } else {
      raceAdditionalPricingItem.vat = undefined;
      raceAdditionalPricingItem.vatExplanation = undefined;
      raceAdditionalPricingItem.vatExplanationEng = undefined;
      raceAdditionalPricingItem.vatExplanationSlo = undefined;
      raceAdditionalPricingItem.vatExplanationIta = undefined;
      raceAdditionalPricingItem.vatExplanationBs = undefined;
      raceAdditionalPricingItem.vatExplanationSr = undefined;
    }
    raceAdditionalPricingItem.price = Number(this.inputForm.value.price);
    let raceRacePricingItemValues: RacePricingItemValue[] = [];
    if (this.currentPricingItemValues.length > 0) {
      for (let i: number = 0; i < this.currentPricingItemValues.length; i++) {
        raceRacePricingItemValues.push(
          new RacePricingItemValue(
            this.currentPricingItemValues[i].id ?? undefined,
            this.currentPricingItemValues[i].itemId ?? undefined,
            this.pricingItemValues.at(i).get('name')!.value,
            this.pricingItemValues.at(i).get('nameEng')!.value,
            this.pricingItemValues.at(i).get('nameIta')!.value,
            this.pricingItemValues.at(i).get('nameSlo')!.value,
            this.pricingItemValues.at(i).get('nameSr')!.value,
            this.pricingItemValues.at(i).get('nameBs')!.value,
            this.pricingItemValues.at(i).get('price')!.value))
      }
    }
    raceAdditionalPricingItem.pricingItemValues = raceRacePricingItemValues;
    return raceAdditionalPricingItem;
  }


  onEditPricingAdditionalItem(raceAdditionalItem: RaceAdditionalPricingItem) {
    this.markAllAsUntouched(this.inputForm);
    this.priceConstantSubscription?.unsubscribe();
    this.inputForm.reset()
    this.inputForm.controls.pricingItemValues = this.formBuilder.array([]);
    this.currentPricingItemValues = raceAdditionalItem.pricingItemValues ? [...raceAdditionalItem.pricingItemValues] : [];
    raceAdditionalItem.priceConstant = raceAdditionalItem.priceConstant === undefined ? true : raceAdditionalItem.priceConstant;
    this.inputForm.patchValue(raceAdditionalItem);
    if (!raceAdditionalItem.fieldRegistrationDisplayFlag) {
      this.inputForm.controls.fieldRegistrationDisplayFlag.setValue(RegistrationDisplayFlags.NOT_USED);
    }
    if (!raceAdditionalItem.fieldVisibility) {
      this.inputForm.controls.fieldVisibility.setValue(VisibilityTypes.NOT_VISIBLE);
    }
    if (this.currentPricingItemValues.length > 0) {
      for (let i: number = 0; i < this.currentPricingItemValues.length; i++) {
        let pricingItemValue = this.createItemValueForm();
        this.pricingItemValues.push(pricingItemValue);
        this.pricingItemValues.at(i).get('price')!.setValue(this.currentPricingItemValues[i].price);
        this.pricingItemValues.at(i).get('name')!.setValue(this.currentPricingItemValues[i].name);
        this.pricingItemValues.at(i).get('nameEng')!.setValue(this.currentPricingItemValues[i].nameEng);
        this.pricingItemValues.at(i).get('nameIta')!.setValue(this.currentPricingItemValues[i].nameIta);
        this.pricingItemValues.at(i).get('nameSlo')!.setValue(this.currentPricingItemValues[i].nameSlo);
        this.pricingItemValues.at(i).get('nameSr')!.setValue(this.currentPricingItemValues[i].nameSr);
        this.pricingItemValues.at(i).get('nameBs')!.setValue(this.currentPricingItemValues[i].nameBs);
      }
    }
    this.setValidatorsOnPricingItemValuesCount();
    this.setValidatorsOnPriceBasedOnPriceConstant(raceAdditionalItem.priceConstant ?? true);
    this.priceConstantSubscription = this.inputForm.controls.priceConstant.valueChanges.subscribe((value: boolean) => {
      this.setValidatorsOnPriceBasedOnPriceConstant(value);
    });
  }

  /**
   * BS - If the VAT explanation is entered, then in addition to the chosen language of the event, it must also be in Croatian.
   */
  getExtraLanguages(): number[] {
    let extraLanguages: number[] = this.event?.extraLanguages ?? [];

    if (!extraLanguages?.includes(Language.HR)) {
      extraLanguages?.push(Language.HR);
    }
    return extraLanguages;
  }

  onDismissClick(): void {
    this.activeModal.dismiss();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.priceConstantSubscription?.unsubscribe();
  }
}
